import { Badge, Button, HStack, Link, Stack, Text, useToast } from "@chakra-ui/react";
import { useAuth } from "../../auth/hooks/useAuth";
import { isAdmin } from "../../auth/lib/roles";
import { useBillingStatusQuery } from "../hooks/useBillingStatusQuery";
import { useCancelSubscriptionMutation } from "../hooks/useCancelSubscriptionMutation";
import { PLAN_LABEL, formatISODate, formatLastAccessDay, formatPlanPrice } from "../lib/plan";
import SubscribeButton from "./SubscribeButton";

const PLAN_COLOR = { trial: "warning", subscriber: "success", free: "brand", read_only: "danger" };

// Seção "Assinatura" do perfil: plano atual, próximo vencimento, fatura em aberto e
// cancelamento. Cancelar mantém o acesso até o fim do período já pago. `origin` vai no
// evento checkout_iniciado (ex.: "email_fim_trial" quando o médico veio do aviso por e-mail).
export default function SubscriptionSection({ origin = "perfil" }) {
  const toast = useToast();
  const { user } = useAuth();
  const statusQuery = useBillingStatusQuery();
  const cancel = useCancelSubscriptionMutation();

  const status = statusQuery.data;
  const plan = status?.plan ?? user?.plan;
  const unavailable = statusQuery.error?.response?.status === 503;
  const paidUntil = formatLastAccessDay(status?.subscription_ends_at ?? user?.subscription_ends_at);

  const handleCancel = () => {
    const ate = paidUntil ? ` Seu acesso continua até ${paidUntil}.` : "";
    if (!window.confirm(`Cancelar a assinatura? Não haverá novas cobranças.${ate}`)) {
      return;
    }
    cancel.mutate(undefined, {
      onSuccess: () => toast({ title: "Assinatura cancelada", status: "info", duration: 5000 }),
      onError: (error) =>
        toast({
          title: "Não foi possível cancelar a assinatura",
          description: error.response?.data?.mensagem,
          status: "error",
          duration: 5000,
          isClosable: true,
        }),
    });
  };

  return (
    <Stack spacing={3}>
      {plan ? (
        <HStack spacing={2}>
          <Text fontSize="sm">Plano atual:</Text>
          <Badge variant="statusSubtle" colorScheme={PLAN_COLOR[plan]}>
            {PLAN_LABEL[plan]}
          </Badge>
        </HStack>
      ) : null}

      <Text fontSize="sm" color="app-muted-fg">
        Plano Fundador: {formatPlanPrice(status?.price_cents)}, com vínculos ilimitados. Quem assina
        agora mantém esse preço. Sem assinatura, o plano grátis continua com 1 vínculo.
      </Text>

      {status?.recurring ? (
        <Stack spacing={2}>
          <Text fontSize="sm">
            Assinatura mensal ativa.
            {status.next_due_date ? ` Próximo vencimento: ${formatISODate(status.next_due_date)}.` : ""}
            {paidUntil && plan === "subscriber" ? ` Acesso pago até ${paidUntil}.` : ""}
          </Text>
          <HStack spacing={3} flexWrap="wrap">
            {status.pending_invoice_url ? (
              <Button as={Link} href={status.pending_invoice_url} size="sm" isExternal>
                Pagar fatura em aberto
              </Button>
            ) : null}
            <Button size="sm" variant="outline" isLoading={cancel.isPending} onClick={handleCancel}>
              Cancelar assinatura
            </Button>
          </HStack>
        </Stack>
      ) : (
        <Stack spacing={2} align="flex-start">
          {paidUntil && plan === "subscriber" && !isAdmin(user) ? (
            <Text fontSize="sm">Assinatura cancelada. Seu acesso continua até {paidUntil}.</Text>
          ) : null}
          {unavailable ? (
            <Text fontSize="sm" color="app-muted-fg">
              A assinatura online ainda não está disponível.
            </Text>
          ) : null}
          {!isAdmin(user) ? (
            <SubscribeButton origin={origin} priceCents={status?.price_cents} />
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}
